import { renderToStaticMarkup } from 'react-dom/server'
import type { TreeDto } from '@lib/dto/tree.dto'
import type { TreeProblemDto } from '@lib/dto/tree-problem.dto'
import { formatMinutesShort } from '@lib/helpers/minutes'
import { JobBadge } from '@/field-map/JobBadge'
import { TreeGlyph } from '@/field-map/TreeGlyph'
import { cn } from '@/lib/utils'

/**
 * How much of a pin to draw.
 *
 * The printed map can show a glyph, a number, and four job badges on all 91
 * trees at once because a designer nudged every label by hand on a sheet the
 * size of a poster. A live map cannot: at the zoom where a whole site fits on a
 * phone, those trees are a few pixels apart and the full stack would be an
 * unreadable pile.
 *
 * So detail is a function of zoom — the standard cartographic answer. Zoomed
 * out you get position and species; lean in and the identity and the work
 * appear. Nothing is hidden, it is deferred.
 */
export type MarkerDetail = 'glyph' | 'label' | 'full'

/**
 * Zoom at which numbers appear, then job badges.
 *
 * These are derived from spacing, not taste. Trees on the reference sites sit
 * roughly 8 m apart; at latitude 33° a web-mercator pixel is 0.25 m at z19 and
 * halves with each step, so 8 m is 32px at z19, 45px at z19.5 and 91px at
 * z20.5. A label chip is ~24px wide and a four-badge strip ~100px — which puts
 * the first legible zoom for each almost exactly here. Set them any lower and
 * neighboring pins overprint into an unreadable ribbon.
 */
export const LABEL_ZOOM = 19.5
export const FULL_ZOOM = 20.5

export function detailForZoom(zoom: number): MarkerDetail {
  if (zoom >= FULL_ZOOM) return 'full'
  if (zoom >= LABEL_ZOOM) return 'label'
  return 'glyph'
}

/**
 * The height a badge shield renders at on a pin.
 *
 * The artwork is 14.78 × 16.95pt — drawn for a sheet viewed at better than
 * 100%, where a badge lands around 40px. Rendered at 16px the word on the band
 * collapsed into a smudge and the colored ring thinned to a single pixel, so
 * the whole thing read as a dark blob. 24px is the smallest height where the
 * shield still looks like the shield.
 */
const PIN_BADGE_HEIGHT = 24

/**
 * What a pin needs in order to draw itself.
 *
 * Narrower than `TreeDto` on purpose. The pin reads four fields, and typing the
 * prop as the whole DTO would mean anything wanting to draw one — the proposal
 * document, say, which renders trees captured in a snapshot rather than rows
 * read from the database — had to manufacture ids and timestamps it does not
 * have and the pin never looks at.
 */
export type MarkerTree = Pick<
  TreeDto,
  'label' | 'species' | 'jobs' | 'estimatedLaborMinutes'
> & {
  /** Only whether there are any is drawn, so only their ids are asked for. */
  problems: readonly Pick<TreeProblemDto, 'id'>[]
}

export interface TreeMarkerProps {
  tree: MarkerTree
  detail: MarkerDetail
  selected: boolean
  /** True while this pin is being repositioned, which changes its affordance. */
  moving: boolean
  /**
   * The tree sits outside the site's drawn boundary.
   *
   * Advisory, never an error. A tree really can be outside the line — a street
   * tree the client maintains, or a boundary traced roughly — so this marks the
   * pin for a second look rather than declaring it wrong.
   */
  outsideBoundary: boolean
  /**
   * Draw this pin in gray.
   *
   * Used while hunting trees that still have no time estimate: the ones that
   * do are washed out so the unfinished ones keep their species color.
   */
  dimmed: boolean
}

/**
 * The pin as it appears on the map: a species glyph centered exactly on the
 * tree's coordinate, with the label and job strip hanging beneath it.
 *
 * The glyph is anchored rather than the whole stack, so growing the pin from
 * `glyph` to `full` never shifts the mark off the tree it points at.
 */
export function TreeMarker({
  tree,
  detail,
  selected,
  moving,
  outsideBoundary,
  dimmed,
}: TreeMarkerProps) {
  const showLabel = detail !== 'glyph'
  const showJobs = detail === 'full' && tree.jobs.length > 0
  /*
   * Hours ride in the label chip rather than on a line of their own, so the pin
   * gains width instead of height — a taller pin pushes its badges further from
   * the tree it points at.
   *
   * Only at the full tier, and that is a spacing decision rather than a
   * preference. "#41" is about 24px; "#41 · 45m" is about 52px. At the label
   * tier neighboring trees are ~64px apart, so the wider chips would touch;
   * one zoom further in they are ~128px apart and there is room to spare.
   */
  const estimate =
    detail === 'full' ? formatMinutesShort(tree.estimatedLaborMinutes) : null

  return (
    <div className="relative">
      {/*
        A 36px transparent hit area around an 18px glyph. Pins are tapped with a
        thumb, in sunlight, by someone standing up — the mark can be small, but
        the target it answers to cannot be.
      */}
      <div className="absolute top-0 left-0 flex h-9 w-9 -translate-x-1/2 -translate-y-1/2 items-center justify-center">
        {selected ? (
          <span
            className={cn(
              'absolute h-7 w-7 rounded-full border-2',
              moving
                ? 'animate-pulse border-danger bg-danger-subtle/60'
                : 'border-brand bg-brand-subtle/70',
            )}
          />
        ) : null}
        <span className={cn(dimmed && 'opacity-40 grayscale')}>
          <TreeGlyph species={tree.species} size={moving ? 22 : 18} />
        </span>
        {/*
          Two advisories, two corners, two shapes.

          Both are separate marks rather than a recoloured glyph: hue and shape
          already carry species, and overloading either would make a flagged
          tree look like a different one. They are kept apart because a tree can
          easily be both — off the property AND defective — and a single badge
          slot would silently drop one of them.

          Shape does the distinguishing, not just color: a triangle for a
          problem with the tree, a dot for a problem with its position. That
          holds up at 8px, in sunlight, and for a color-blind reader.
        */}
        {/*
          One mark however many problems a tree has. The pin answers "is there
          something to look at here", and a count or a per-problem mark would
          make a 12mm square carry detail that belongs in the sheet.
        */}
        {tree.problems.length > 0 ? <ProblemMark /> : null}
        {outsideBoundary ? (
          <span className="absolute right-1.5 bottom-1 block h-2 w-2 rounded-full border border-map-halo bg-warning" />
        ) : null}
      </div>

      {showLabel ? (
        <div className="pointer-events-none absolute top-[11px] left-0 flex -translate-x-1/2 flex-col items-center gap-[2px]">
          <span
            className={cn(
              'rounded-[3px] border px-[3px] text-[10px] leading-[13px] font-semibold whitespace-nowrap text-map-ink',
              selected
                ? 'border-brand bg-map-marker-surface'
                : 'border-default bg-map-marker-surface',
            )}
          >
            {tree.label}
            {estimate === null ? null : (
              <span className="font-normal text-muted-text"> · {estimate}</span>
            )}
          </span>
          {showJobs ? (
            <span className="flex justify-center gap-[1px]">
              {tree.jobs.map((job) => (
                <JobBadge
                  key={job.type}
                  type={job.type}
                  level={job.level}
                  height={PIN_BADGE_HEIGHT}
                  showWord
                />
              ))}
            </span>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

/**
 * Render a pin to the HTML string Leaflet's `divIcon` wants.
 *
 * Leaflet owns these nodes, not React — it creates and destroys them as tiles
 * and viewport change, so mounting a React root per pin would mean 91 roots to
 * keep in step with a DOM that moves underneath them. Static markup sidesteps
 * that entirely: the pin is pure presentation, all behavior lives on the
 * marker's Leaflet events, and the Tailwind classes still resolve because the
 * stylesheet is global.
 */
/**
 * The problem badge: a haloed triangle at the pin's top-right.
 *
 * Drawn as SVG rather than a bordered div because a triangle has no CSS
 * primitive, and because the halo has to follow the outline rather than a
 * bounding box — the same reason the species glyph is SVG. `danger` rather than
 * `warning`: the boundary dot is already amber, and two amber marks on one pin
 * would need reading rather than glancing at.
 */
function ProblemMark() {
  return (
    <svg
      viewBox="0 0 24 24"
      width={11}
      height={11}
      className="absolute top-0.5 right-0.5"
      aria-hidden="true"
      focusable="false"
    >
      <path
        d="M12 3.5 22 20.5H2z"
        className="fill-danger stroke-map-halo"
        strokeWidth={3}
        strokeLinejoin="round"
        paintOrder="stroke fill"
      />
    </svg>
  )
}

export function treeMarkerHtml(props: TreeMarkerProps): string {
  return renderToStaticMarkup(<TreeMarker {...props} />)
}

/**
 * Everything that changes what this pin LOOKS like, as one comparable string.
 *
 * Rendering a pin means running React to a string and replacing the marker's
 * DOM, which a survey screen was doing for all ninety of them on every
 * keystroke. This is what lets the map redraw only the pin that actually
 * changed.
 *
 * It lives beside the component rather than at the call site on purpose: it
 * has to cover exactly what `TreeMarker` reads, no less. `MarkerTree` picks
 * five fields and this covers all five, so a sixth added to the pin fails to
 * compile here until it is accounted for — and a field the pin does NOT read,
 * like `notes`, is deliberately absent, because typing a note should not
 * redraw anything.
 */
export function treeMarkerSignature(props: TreeMarkerProps): string {
  const { tree } = props
  const species =
    tree.species === null
      ? '-'
      : `${tree.species.name}/${tree.species.shape}/${tree.species.color}`
  return [
    tree.label,
    species,
    tree.estimatedLaborMinutes ?? '-',
    // The pin shows one mark however many problems there are, so the count is
    // all it can distinguish — but a count, not a boolean, so going from one
    // problem to two is still a change if that ever starts to matter.
    tree.problems.length,
    tree.jobs.map((job) => `${job.type}${String(job.level)}`).join(','),
    props.detail,
    props.selected ? 'sel' : '',
    props.moving ? 'mov' : '',
    props.outsideBoundary ? 'out' : '',
    props.dimmed ? 'dim' : '',
  ].join('~')
}
